const API_BASE_URL = String(
  process.env.REACT_APP_API_BASE_URL || '',
).replace(/\/$/, '');

const parsedTimeout = Number(
  process.env.REACT_APP_REQUEST_TIMEOUT_MS || 10000,
);

const REQUEST_TIMEOUT_MS =
  Number.isFinite(parsedTimeout) &&
  parsedTimeout > 0
    ? parsedTimeout
    : 10000;

let unauthorizedHandler = null;

export class ApiError extends Error {
  constructor(
    message,
    {
      status = 0,
      code = 'UNKNOWN_ERROR',
      data = null,
    } = {},
  ) {
    super(message);

    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.data = data;
  }
}

export function setUnauthorizedHandler(
  handler,
) {
  unauthorizedHandler =
    typeof handler === 'function'
      ? handler
      : null;
}

function buildApiUrl(endpoint) {
  if (/^https?:\/\//i.test(endpoint)) {
    return endpoint;
  }

  const normalizedEndpoint =
    endpoint.startsWith('/')
      ? endpoint
      : `/${endpoint}`;

  return `${API_BASE_URL}${normalizedEndpoint}`;
}

async function parseResponseBody(response) {
  if (response.status === 204) {
    return null;
  }

  const contentType =
    response.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    try {
      return await response.json();
    } catch {
      return null;
    }
  }

  try {
    return await response.text();
  } catch {
    return null;
  }
}

function getResponseErrorMessage(responseData) {
  if (
    responseData &&
    typeof responseData === 'object' &&
    typeof responseData.message === 'string'
  ) {
    return responseData.message;
  }

  if (
    typeof responseData === 'string' &&
    responseData.trim()
  ) {
    return responseData;
  }

  return 'Request failed';
}

function getResponseErrorCode(
  response,
  responseData,
) {
  if (
    responseData &&
    typeof responseData === 'object'
  ) {
    return (
      responseData.errorCode ||
      responseData.code ||
      `HTTP_${response.status}`
    );
  }

  return `HTTP_${response.status}`;
}

export async function requestJson(
  endpoint,
  options = {},
  meta = {},
) {
  const controller = new AbortController();

  const timeoutId = window.setTimeout(() => {
    controller.abort();
  }, REQUEST_TIMEOUT_MS);

  const headers = {
    Accept: 'application/json',
    ...(options.body
      ? { 'Content-Type': 'application/json' }
      : {}),
    ...options.headers,
  };

  const skipUnauthorizedHandler =
    meta?.skipUnauthorizedHandler === true;

  try {
    const response = await fetch(
      buildApiUrl(endpoint),
      {
        ...options,
        headers,
        credentials: 'include',
        cache: 'no-store',
        signal: controller.signal,
      },
    );

    const responseData =
      await parseResponseBody(response);

    if (!response.ok) {
      const apiError = new ApiError(
        getResponseErrorMessage(
          responseData,
        ),
        {
          status: response.status,
          code: getResponseErrorCode(
            response,
            responseData,
          ),
          data: responseData,
        },
      );

      if (
        response.status === 401 &&
        !skipUnauthorizedHandler &&
        typeof unauthorizedHandler ===
          'function'
      ) {
        unauthorizedHandler(apiError);
      }

      throw apiError;
    }

    return responseData;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    if (error?.name === 'AbortError') {
      throw new ApiError(
        'Request timeout',
        {
          code: 'REQUEST_TIMEOUT',
        },
      );
    }

    throw new ApiError('Network error', {
      code: 'NETWORK_ERROR',
    });
  } finally {
    window.clearTimeout(timeoutId);
  }
}
